import { useEffect, useState } from "react";
import {
    Button,
    Card,
    DatePicker,
    InputNumber,
    Modal,
    Select,
    Space,
    Spin,
    message,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";

import {
    PROJECT_REVIEW_HEALTH_API_URL,
    PROJECT_REVIEW_BASE_URL,
} from "../config/constants";

import { useAppDispatch } from "../store/hooks";
import { setProjectHealthStatus } from "../store/slices/ProjectHealthStatusSlice";
import { apiFetch } from "../utils/apiClient";

import "../styles/trackon.css";

// ─── Constants ───────────────────────────────────────────────────────────────

const DATE_DISPLAY_FORMAT = "MM-DD-YYYY";

const FIELD_LABEL = {
    scope: "Scope",
    schedule: "Schedule",
    quality: "Quality",
    riskIssue: "Risk/Issue",
    resource: "Resource",
    invoiceStatus: "Invoice",
};

const RAG_VISUAL = {
    G: {
        color: "#16a34a",
        label: "On Track",
        border: "#bbf7d0",
        bg: "#f0fdf4",
    },
    A: {
        color: "#d97706",
        label: "Needs Attention",
        border: "#fcd9a5",
        bg: "#fffbeb",
    },
    R: {
        color: "#dc2626",
        label: "At Risk",
        border: "#fecaca",
        bg: "#fef2f2",
    },
    B: {
        color: "#1f2937",
        label: "Not Updated",
        border: "#d1d5db",
        bg: "#f9fafb",
    },
    N: {
        color: "#6b7280",
        label: "Not Updated",
        border: "#e1e4eb",
        bg: "#ffffff",
    },
};

const RAG_OPTIONS = ["G", "A", "R", "B"];

const RAG_SELECT_LABEL = {
    G: "On Track",
    A: "Needs Attention",
    R: "At Risk",
    B: "Not Updated",
    N: "Not Updated",
};

const SUPPORT_FLAG_OPTIONS = [
    { value: 1, label: "Black (Not Updated)" },
    { value: 4, label: "Green (On Track)" },
    { value: 3, label: "Amber (Needs Attention)" },
    { value: 2, label: "Red (At Risk)" },
];

const RATING_OPTIONS = [
    { value: 4, label: "On Track" },
    { value: 3, label: "Needs Attention" },
    { value: 2, label: "At Risk" },
    { value: 1, label: "Not Updated" },
];

const DEFAULT_FIELDS = {
    scope: "B",
    schedule: "B",
    quality: "B",
    riskIssue: "B",
    resource: "B",
    invoiceStatus: "B",
};

const DEFAULT_RATINGS = {
    pmoRating: null,
    pmRating: null,
    supportFromWm: null,
    supportFromUst: null,
};

// ─── Date Helpers ────────────────────────────────────────────────────────────

const parseBackendDate = (value) => {
    if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
    ) {
        return null;
    }

    const raw = String(value).trim();

    // MM-DD-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
        const parsed = dayjs(raw, "MM-DD-YYYY", true);

        return parsed.isValid() ? parsed : null;
    }

    // MM/DD/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const parsed = dayjs(raw, "MM/DD/YYYY", true);

        return parsed.isValid() ? parsed : null;
    }

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const parsed = dayjs(raw, "YYYY-MM-DD", true);

        return parsed.isValid() ? parsed : null;
    }

    // ISO / timestamp / other backend formats
    const parsed = dayjs(raw);

    return parsed.isValid() ? parsed : null;
};

const formatDateForApi = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs.isDayjs(value)
        ? value
        : parseBackendDate(value);

    if (!parsed || !parsed.isValid()) {
        return null;
    }

    return parsed.format(DATE_DISPLAY_FORMAT);
};

const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs(), "day");
};

// ─── General Helpers ─────────────────────────────────────────────────────────

const normalizeRag = (value) => {
    const normalized = String(value || "")
        .trim()
        .toUpperCase();

    if (normalized === "G") return "G";
    if (normalized === "A") return "A";
    if (normalized === "R") return "R";
    if (normalized === "B") return "B";

    return "B";
};

const mapRatingToRag = (rating) => {
    if (rating === 4) return "G";
    if (rating === 3) return "A";
    if (rating === 2) return "R";
    if (rating === 1) return "B";

    return "B";
};

const getAuthHeader = () => {
    const token =
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("accessToken");

    if (!token) {
        return undefined;
    }

    const normalizedToken = token
        .replace(/^"|"$/g, "")
        .replace(/^Bearer\s+/i, "")
        .trim();

    if (!normalizedToken) {
        return undefined;
    }

    return `Bearer ${normalizedToken}`;
};

const getCurrentUserRole = () => {
    const role =
        sessionStorage.getItem("userRole") ||
        sessionStorage.getItem("role") ||
        "";

    return String(role).trim();
};

const canEditHealthStatus = (role) => {
    const normalized = role.toUpperCase();

    return (
        normalized === "PMO" ||
        normalized === "ACCOUNT MANAGER" ||
        normalized === "ADMIN"
    );
};

// ─── Presentational Helpers ──────────────────────────────────────────────────

const MetricCard = ({ label, children, border, bg }) => (
    <div
        style={{
            border: `1px solid ${border}`,
            borderRadius: 10,
            padding: "8px 4px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
            background: `linear-gradient(180deg, ${border} 0%, ${bg} 45%, #ffffff 100%)`,
            boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
            transition: "all 0.2s ease",
        }}
    >
        <div
            style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#8b92a5",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                textAlign: "center",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
            title={label}
        >
            {label}
        </div>

        {children}
    </div>
);

const RagBadge = ({ rag }) => {
    const visual = RAG_VISUAL[rag] || RAG_VISUAL.B;

    return (
        <>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${visual.color}, ${visual.color}cc)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 8px ${visual.color}44`,
                }}
            >
                <span
                    style={{
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 800,
                    }}
                >
                    {rag}
                </span>
            </div>

            <div
                style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: visual.color,
                    textAlign: "center",
                    lineHeight: 1.2,
                }}
            >
                {visual.label}
            </div>
        </>
    );
};

const SupportBadge = ({ rag }) => {
    const visual = RAG_VISUAL[rag] || RAG_VISUAL.B;

    return (
        <>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${visual.color}, ${visual.color}cc)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 2px 8px ${visual.color}44`,
                }}
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="#ffffff"
                    stroke="#ffffff"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M5 2v16M5 2l10 4-10 4" />
                </svg>
            </div>

            <div
                style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: visual.color,
                    textAlign: "center",
                    lineHeight: 1.2,
                }}
            >
                {visual.label}
            </div>
        </>
    );
};

const FormField = ({ label, children }) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
        }}
    >
        <span
            style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#4b5563",
            }}
        >
            {label}
        </span>

        {children}
    </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

export default function ProjectHealthStatus({
    projectId,
    readOnly = false,
}) {
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [original, setOriginal] = useState(DEFAULT_FIELDS);
    const [draft, setDraft] = useState(DEFAULT_FIELDS);

    const [ratings, setRatings] = useState(DEFAULT_RATINGS);
    const [draftRatings, setDraftRatings] =
        useState(DEFAULT_RATINGS);

    const [ebitda, setEbitda] = useState(null);
    const [draftEbitda, setDraftEbitda] = useState(null);
    const [plannedEbitda, setPlannedEbitda] = useState(null);

    const [lastReviewDate, setLastReviewDate] =
        useState(null);

    const [draftLastReviewDate, setDraftLastReviewDate] =
        useState(null);

    const currentUserRole = getCurrentUserRole();

    const canEdit =
        !readOnly &&
        canEditHealthStatus(currentUserRole);

    // ─── Fetch Health Status ────────────────────────────────────────────────

    useEffect(() => {
        if (!projectId) {
            return;
        }

        const controller = new AbortController();

        const fetchHealth = async () => {
            setLoading(true);

            try {
                const requestUrl = new URL(
                    PROJECT_REVIEW_HEALTH_API_URL,
                    globalThis.location.origin
                );

                requestUrl.searchParams.set("page", "0");
                requestUrl.searchParams.set("size", "10");
                requestUrl.searchParams.set(
                    "searchText",
                    projectId
                );

                const authHeader = getAuthHeader();

                const response = await apiFetch(
                    requestUrl.toString(),
                    {
                        method: "GET",
                        headers: {
                            ...(authHeader
                                ? {
                                      Authorization:
                                          authHeader,
                                  }
                                : {}),
                            Accept: "application/json",
                        },
                        signal: controller.signal,
                    },
                    "Failed to fetch health status",
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch health status (${response.status})`
                    );
                }

                const payload = await response.json();

                const list = Array.isArray(
                    payload.projects
                )
                    ? payload.projects
                    : Array.isArray(payload.content)
                    ? payload.content
                    : [];

                const match =
                    list.find(
                        (item) =>
                            String(
                                item.projectId || ""
                            ) === String(projectId)
                    ) || list[0];

                const nextFields = match
                    ? {
                          scope: normalizeRag(
                              match.scope
                          ),
                          schedule: normalizeRag(
                              match.schedule
                          ),
                          quality: normalizeRag(
                              match.quality
                          ),
                          riskIssue: normalizeRag(
                              match.riskIssue
                          ),
                          resource: normalizeRag(
                              match.resource
                          ),
                          invoiceStatus:
                              normalizeRag(
                                  match.invoiceStatus
                              ),
                      }
                    : { ...DEFAULT_FIELDS };

                const nextRatings = match
                    ? {
                          pmoRating:
                              match.pmoRating ??
                              null,

                          pmRating:
                              match.pmRating ??
                              null,

                          supportFromWm:
                              match.supportFromWm ??
                              null,

                          supportFromUst:
                              match.supportFromUst ??
                              null,
                      }
                    : { ...DEFAULT_RATINGS };

                const nextEbitda =
                    match?.actualEbitda ?? null;
                const nextPlannedEbitda =
                    match?.plannedEbitda ?? null;

                const nextLastReviewDate = match
                    ? parseBackendDate(
                          match.reviewDate
                      )
                    : null;

                const reduxLastReviewDate =
                    match?.reviewDate
                        ? formatDateForApi(
                              match.reviewDate
                          )
                        : null;

                // ─── Update local state ───────────────

                setOriginal(nextFields);
                setDraft(nextFields);

                setRatings(nextRatings);
                setDraftRatings(nextRatings);

                setEbitda(nextEbitda);

                setPlannedEbitda(
                    nextPlannedEbitda
                );

                setLastReviewDate(
                    nextLastReviewDate
                );

                setDraftLastReviewDate(
                    nextLastReviewDate
                );

                dispatch(
                    setProjectHealthStatus({
                        projectId,
                        healthStatus: {
                            scope: nextFields.scope,
                            schedule:
                                nextFields.schedule,
                            quality:
                                nextFields.quality,
                            riskIssue:
                                nextFields.riskIssue,
                            resource:
                                nextFields.resource,
                            invoiceStatus:
                                nextFields.invoiceStatus,

                            pmoRating:
                                nextRatings.pmoRating,

                            pmRating:
                                nextRatings.pmRating,

                            supportFromWm:
                                nextRatings.supportFromWm,

                            supportFromUst:
                                nextRatings.supportFromUst,

                            ebitda: nextEbitda,

                            lastReviewDate:
                                reduxLastReviewDate,
                        },
                    })
                );
            } catch (error) {
                if (
                    error?.name ===
                    "AbortError"
                ) {
                    return;
                }

                setOriginal({
                    ...DEFAULT_FIELDS,
                });

                setDraft({
                    ...DEFAULT_FIELDS,
                });

                setRatings({
                    ...DEFAULT_RATINGS,
                });

                setDraftRatings({
                    ...DEFAULT_RATINGS,
                });

                setEbitda(null);

                setPlannedEbitda(null);

                setLastReviewDate(null);

                setDraftLastReviewDate(null);
            } finally {
                setLoading(false);
            }
        };

        void fetchHealth();

        return () => {
            controller.abort();
        };
    }, [projectId, dispatch]);

    // ─── Field Changes ─────────────────────────────────────────────────────

    const handleFieldChange = (
        field,
        value
    ) => {
        setDraft((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRatingChange = (
        field,
        value
    ) => {
        setDraftRatings((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // ─── Editing ───────────────────────────────────────────────────────────

    const startEditing = () => {
        setDraft({
            ...original,
        });

        setDraftRatings({
            ...ratings,
        });

        setDraftEbitda(ebitda);

        setDraftLastReviewDate(
            lastReviewDate
        );

        setModalOpen(true);
    };

    const cancelEditing = () => {
        setDraft({
            ...original,
        });

        setDraftRatings({
            ...ratings,
        });

        setDraftEbitda(ebitda);

        setDraftLastReviewDate(
            lastReviewDate
        );

        setModalOpen(false);
    };

    // ─── Save ───────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!projectId) {
            return;
        }

        setSaving(true);

        try {
            const normalizedEbitda =
                draftEbitda === null ||
                draftEbitda === undefined ||
                String(draftEbitda).trim() === ""
                    ? null
                    : Number(
                          String(
                              draftEbitda
                          ).replace(
                              /%$/,
                              ""
                          )
                      );

            /*
             * Convert DatePicker value to:
             * MM-DD-YYYY
             */
            const formattedLastReviewDate =
                formatDateForApi(
                    draftLastReviewDate
                );

            const payload = {
                scope: draft.scope,

                schedule: draft.schedule,

                quality: draft.quality,

                riskIssue: draft.riskIssue,

                resource: draft.resource,

                invoiceStatus:
                    draft.invoiceStatus,

                supportFromWm:
                    draftRatings.supportFromWm,

                supportFromUst:
                    draftRatings.supportFromUst,

                pmoRating:
                    draftRatings.pmoRating,

                pmRating:
                    draftRatings.pmRating,

                ebitda: normalizedEbitda,

                lastReviewDate:
                    formattedLastReviewDate,
            };

            const authHeader =
                getAuthHeader();

            const response = await apiFetch(
                `${PROJECT_REVIEW_BASE_URL}/${projectId}/status`,
                {
                    method: "PATCH",

                    headers: {
                        ...(authHeader
                            ? {
                                  Authorization:
                                      authHeader,
                              }
                            : {}),

                        "Content-Type":
                            "application/json",

                        Accept:
                            "application/json",
                    },

                    body: JSON.stringify(
                        payload
                    ),
                },
                "Failed to update health status",
            );

            if (!response.ok) {
                const errorData =
                    await response
                        .json()
                        .catch(
                            () => ({})
                        );

                throw new Error(
                    errorData.message ||
                        `Failed to update health status (${response.status})`
                );
            }

            // ─── Update local state ─────────────────

            setOriginal({
                ...draft,
            });

            setRatings({
                ...draftRatings,
            });

            setEbitda(
                normalizedEbitda
            );

            setLastReviewDate(
                draftLastReviewDate
                    ? dayjs(
                          draftLastReviewDate
                      )
                    : null
            );

            // ─── Update Redux ──────────────────────

            dispatch(
                setProjectHealthStatus({
                    projectId,

                    healthStatus: {
                        scope: draft.scope,

                        schedule:
                            draft.schedule,

                        quality:
                            draft.quality,

                        riskIssue:
                            draft.riskIssue,

                        resource:
                            draft.resource,

                        invoiceStatus:
                            draft.invoiceStatus,

                        pmoRating:
                            draftRatings.pmoRating,

                        pmRating:
                            draftRatings.pmRating,

                        supportFromWm:
                            draftRatings.supportFromWm,

                        supportFromUst:
                            draftRatings.supportFromUst,

                        ebitda:
                            normalizedEbitda,

                        /*
                         * Redux gets a serializable string,
                         * not a dayjs object.
                         */
                        lastReviewDate:
                            formattedLastReviewDate,
                    },
                })
            );

            setModalOpen(false);

            message.success(
                "Health status updated successfully!"
            );
        } catch (error) {
            message.error(
                error instanceof Error
                    ? error.message
                    : "Failed to update health status"
            );
        } finally {
            setSaving(false);
        }
    };

    // ─── Loading ───────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Card
                style={{
                    borderRadius: 14,
                    border: "1px solid #e6ecf5",
                    boxShadow:
                        "0 6px 18px rgba(16,24,40,0.03)",
                }}
                styles={{
                    body: {
                        padding: 18,
                    },
                }}
            >
                <div
                    style={{
                        minHeight: 160,
                        display: "flex",
                        justifyContent:
                            "center",
                        alignItems: "center",
                    }}
                >
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    const fieldValues = original;

    const ratingValues = ratings;

    const overallRag =
        mapRatingToRag(
            ratingValues.pmoRating
        );

    const pmRag =
        mapRatingToRag(
            ratingValues.pmRating
        );

    // ─── EBITDA ────────────────────────────────────────────────────────────

    const hasEbitda =
        ebitda !== null &&
        ebitda !== undefined &&
        String(ebitda).trim() !== "";

    const numericEbitda = hasEbitda
        ? Number(
              String(ebitda)
                  .trim()
                  .replace(
                      /%$/,
                      ""
                  )
          )
        : Number.NaN;

    const isValidEbitda =
        !Number.isNaN(
            numericEbitda
        );

    const hasPlannedEbitda =
        plannedEbitda !== null &&
        plannedEbitda !== undefined &&
        String(plannedEbitda).trim() !== "";

    const numericPlannedEbitda =
        hasPlannedEbitda
            ? Number(
                  String(plannedEbitda)
                      .trim()
                      .replace(
                          /%$/,
                          ""
                      )
              )
            : Number.NaN;

    const isValidPlannedEbitda =
        !Number.isNaN(
            numericPlannedEbitda
        );


    const isBelowThreshold =
        isValidEbitda &&
        (
            isValidPlannedEbitda
                ? numericEbitda <
                  numericPlannedEbitda
                : numericEbitda < 20
        );

    const ebitdaColor =
        isBelowThreshold
            ? "#dc2626"
            : "#16a34a";

    const ebitdaBorder =
        !isValidEbitda
            ? "#e1e4eb"
            : isBelowThreshold
            ? "#fecaca"
            : "#bbf7d0";

    const ebitdaBg =
        !isValidEbitda
            ? "#ffffff"
            : isBelowThreshold
            ? "#fef2f2"
            : "#f0fdf4";

    // ─── Render ────────────────────────────────────────────────────────────

    return (
        <Card
            style={{
                borderRadius: 14,
                border: "1px solid #e6ecf5",
                boxShadow:
                    "0 6px 18px rgba(16,24,40,0.03)",
            }}
            styles={{
                body: {
                    padding: 16,
                },
            }}
        >
            {canEdit && (
                <div
                    style={{
                        display: "flex",
                        justifyContent:
                            "flex-end",
                        alignItems: "center",
                        marginBottom: 10,
                    }}
                >
                    <Button
                        size="small"
                        icon={
                            <EditOutlinedIcon
                                style={{
                                    fontSize: 16,
                                }}
                            />
                        }
                        onClick={
                            startEditing
                        }
                        style={{
                            fontSize: 12,
                            borderRadius: 6,
                        }}
                    />
                </div>
            )}

            {/* ─── Metric Cards ─────────────────────────────────────────── */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fit, minmax(70px, 1fr))",
                    gap: 8,
                }}
            >
                {/* Overall */}

                <MetricCard
                    label={
                        <span
                            style={{
                                color: "#000",
                            }}
                        >
                            Overall
                        </span>
                    }
                    border={
                        RAG_VISUAL[
                            overallRag
                        ].border
                    }
                    bg={
                        RAG_VISUAL[
                            overallRag
                        ].bg
                    }
                >
                    <RagBadge
                        rag={overallRag}
                    />
                </MetricCard>

                {/* PM */}

                <MetricCard
                    label={
                        <span
                            style={{
                                color: "#000",
                            }}
                        >
                            PM
                        </span>
                    }
                    border={
                        RAG_VISUAL[
                            pmRag
                        ].border
                    }
                    bg={
                        RAG_VISUAL[
                            pmRag
                        ].bg
                    }
                >
                    <RagBadge
                        rag={pmRag}
                    />
                </MetricCard>

                {/* RAG metrics */}

                {Object.keys(
                    FIELD_LABEL
                ).map((field) => {
                    const value =
                        fieldValues[
                            field
                        ];

                    const visual =
                        RAG_VISUAL[
                            value
                        ] ||
                        RAG_VISUAL.B;

                    return (
                        <MetricCard
                            key={
                                field
                            }
                            label={
                                <span
                                    style={{
                                        color: "#000",
                                    }}
                                >
                                    {
                                        FIELD_LABEL[
                                            field
                                        ]
                                    }
                                </span>
                            }
                            border={
                                visual.border
                            }
                            bg={
                                visual.bg
                            }
                        >
                            <RagBadge
                                rag={
                                    value
                                }
                            />
                        </MetricCard>
                    );
                })}

                {/* Support flags */}

                {[
                    {
                        key: "supportFromUst",
                        label: "UST Support",
                    },
                    {
                        key: "supportFromWm",
                        label: "WM Support",
                    },
                ].map(
                    ({
                        key,
                        label,
                    }) => {
                        const rating =
                            ratingValues[
                                key
                            ];

                        const rag =
                            mapRatingToRag(
                                rating ??
                                    null
                            );

                        const visual =
                            RAG_VISUAL[
                                rag
                            ];

                        return (
                            <MetricCard
                                key={
                                    key
                                }
                                label={
                                    <span
                                        style={{
                                            color: "#000",
                                        }}
                                    >
                                        {
                                            label
                                        }
                                    </span>
                                }
                                border={
                                    visual.border
                                }
                                bg={
                                    visual.bg
                                }
                            >
                                <SupportBadge
                                    rag={
                                        rag
                                    }
                                />
                            </MetricCard>
                        );
                    }
                )}

                {/* EBITDA */}

                <MetricCard
                    label={
                        <span
                            style={{
                                color: "#000",
                            }}
                        >
                            EBITDA %
                        </span>
                    }
                    border={
                        ebitdaBorder
                    }
                    bg={
                        ebitdaBg
                    }
                >
                    <div
                        style={{
                            minWidth: 30,
                            height: 30,
                            padding:
                                "0 6px",
                            borderRadius: 8,
                            background:
                                isValidEbitda
                                    ? `linear-gradient(135deg, ${ebitdaColor}, ${ebitdaColor}cc)`
                                    : "#e5e7eb",
                            display:
                                "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            boxShadow:
                                isValidEbitda
                                    ? `0 2px 8px ${ebitdaColor}44`
                                    : "none",
                        }}
                    >
                        <span
                            title={
                                isValidEbitda
                                    ? `Actual: ${numericEbitda}%${
                                          isValidPlannedEbitda
                                              ? ` | Planned: ${numericPlannedEbitda}%`
                                              : ""
                                      }`
                                    : ""
                            }
                            style={{
                                color:
                                    isValidEbitda
                                        ? "#fff"
                                        : "#9ca3af",
                                fontSize: 12,
                                fontWeight: 800,
                            }}
                        >
                            {isValidEbitda
                                ? `${numericEbitda}`
                                : "—"}
                        </span>
                    </div>

                    <div
                        style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color:
                                isValidEbitda
                                    ? ebitdaColor
                                    : "#9ca3af",
                            textAlign:
                                "center",
                            lineHeight: 1.2,
                        }}
                    >
                        {isValidEbitda
                            ? isBelowThreshold
                                ? "Below Target"
                                : "On Target"
                            : "N/A"}
                    </div>
                </MetricCard>
            </div>

            {/* ─── Edit Modal ────────────────────────────────────────────── */}

            <Modal
                className="trackon-root"
                open={modalOpen}
                onCancel={
                    cancelEditing
                }
                width={720}
                centered
                destroyOnClose
                maskClosable={
                    !saving
                }
                title={
                    <div
                        style={{
                            display:
                                "flex",
                            alignItems:
                                "center",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 9,
                                background:
                                    "linear-gradient(135deg,#1677ff,#4096ff)",
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "center",
                                boxShadow:
                                    "0 2px 8px rgba(22,119,255,0.35)",
                            }}
                        >
                            <EditOutlinedIcon
                                style={{
                                    color:
                                        "#fff",
                                    fontSize: 16,
                                }}
                            />
                        </div>

                        <div>
                            <div
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#1f2937",
                                }}
                            >
                                Edit Health
                                Status
                            </div>

                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#8b92a5",
                                    fontWeight: 400,
                                }}
                            >
                                Update
                                ratings and
                                metrics for
                                this project
                            </div>
                        </div>
                    </div>
                }
                footer={
                    <Space>
                        <Button
                            icon={
                                <CloseOutlined />
                            }
                            onClick={
                                cancelEditing
                            }
                            disabled={
                                saving
                            }
                            style={{
                                borderRadius: 6,
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            icon={
                                <CheckOutlined />
                            }
                            loading={
                                saving
                            }
                            onClick={
                                handleSave
                            }
                            style={{
                                borderRadius: 6,
                                background:
                                    "linear-gradient(135deg,#1677ff,#4096ff)",
                                border: "none",
                            }}
                        >
                            Save Changes
                        </Button>
                    </Space>
                }
            >
                <div
                    style={{
                        display:
                            "grid",
                        gridTemplateColumns:
                            "repeat(2, minmax(0, 1fr))",
                        gap: 16,
                        marginTop: 8,
                    }}
                >
                    {/* Overall */}

                    <FormField label="Overall (PMO Rating)">
                        <Select
                            value={
                                draftRatings.pmoRating ??
                                undefined
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="Select rating"
                            onChange={(
                                value
                            ) =>
                                handleRatingChange(
                                    "pmoRating",
                                    value
                                )
                            }
                            options={[
                                ...RATING_OPTIONS,
                            ]}
                        />
                    </FormField>

                    {/* PM */}

                    <FormField label="PM Rating">
                        <Select
                            value={
                                draftRatings.pmRating ??
                                undefined
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="Select rating"
                            onChange={(
                                value
                            ) =>
                                handleRatingChange(
                                    "pmRating",
                                    value
                                )
                            }
                            options={[
                                ...RATING_OPTIONS,
                            ]}
                        />
                    </FormField>

                    {/* RAG metrics */}

                    {Object.keys(
                        FIELD_LABEL
                    ).map(
                        (
                            field
                        ) => (
                            <FormField
                                key={
                                    field
                                }
                                label={
                                    FIELD_LABEL[
                                        field
                                    ]
                                }
                            >
                                <Select
                                    value={
                                        draft[
                                            field
                                        ]
                                    }
                                    style={{
                                        width:
                                            "100%",
                                    }}
                                    onChange={(
                                        newValue
                                    ) =>
                                        handleFieldChange(
                                            field,
                                            newValue
                                        )
                                    }
                                    options={RAG_OPTIONS.map(
                                        (
                                            option
                                        ) => ({
                                            value:
                                                option,
                                            label:
                                                RAG_SELECT_LABEL[
                                                    option
                                                ],
                                        })
                                    )}
                                />
                            </FormField>
                        )
                    )}

                    {/* WM Support */}

                    <FormField label="WM Support">
                        <Select
                            value={
                                draftRatings.supportFromWm ??
                                undefined
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="Select rating"
                            onChange={(
                                value
                            ) =>
                                handleRatingChange(
                                    "supportFromWm",
                                    value
                                )
                            }
                            options={[
                                ...SUPPORT_FLAG_OPTIONS,
                            ]}
                        />
                    </FormField>

                    {/* UST Support */}

                    <FormField label="UST Support">
                        <Select
                            value={
                                draftRatings.supportFromUst ??
                                undefined
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="Select rating"
                            onChange={(
                                value
                            ) =>
                                handleRatingChange(
                                    "supportFromUst",
                                    value
                                )
                            }
                            options={[
                                ...SUPPORT_FLAG_OPTIONS,
                            ]}
                        />
                    </FormField>

                    {/* EBITDA */}

                    <FormField label="EBITDA %">
                        <InputNumber
                            value={
                                draftEbitda ===
                                    null ||
                                draftEbitda ===
                                    undefined ||
                                String(
                                    draftEbitda
                                ).trim() ===
                                    ""
                                    ? undefined
                                    : Number(
                                          String(
                                              draftEbitda
                                          ).replace(
                                              /%$/,
                                              ""
                                          )
                                      )
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="Enter EBITDA %"
                            onChange={(
                                value
                            ) =>
                                setDraftEbitda(
                                    value ??
                                        null
                                )
                            }
                        />
                    </FormField>

                    {/* Last Review Date */}

                    <FormField label="Last Review Date">
                        <DatePicker
                            value={
                                draftLastReviewDate
                                    ? dayjs(
                                          draftLastReviewDate
                                      )
                                    : null
                            }
                            format={
                                DATE_DISPLAY_FORMAT
                            }
                            disabledDate={disableFutureDates}
                            style={{
                                width:
                                    "100%",
                            }}
                            placeholder="MM-DD-YYYY"
                            allowClear
                            onChange={(
                                value
                            ) => {
                                setDraftLastReviewDate(
                                    value
                                        ? dayjs(
                                              value
                                          )
                                        : null
                                );
                            }}
                        />
                    </FormField>
                </div>
            </Modal>
        </Card>
    );
}